import { createAsyncThunk } from "@reduxjs/toolkit";
import { displayAlert } from "../slice/alertSlice.tsx";
import campaignsApi from "../api/campaignsApi.tsx";
import { setCampaignsInfo } from "../slice/campaignsSlice.tsx";
import { handleThunkError } from "./handleThunkError";
import {
  CampaignByIdDeleteType,
  CampaignByIdEditDataType,
  CampaignCreateDataType,
  MediaPlanBodyDataType,
} from "../types/campaignsTypes.tsx";

export const fetchCampaignsData = createAsyncThunk(
  "campaigns/fetchCampaignsData",
  async (params: string, thunkAPI) => {
    try {
      const response = await campaignsApi.fetchCampaignsData(params);
      thunkAPI.dispatch(
        setCampaignsInfo({
          in_progress: response.data.in_progress,
          off_track: response.data.off_track,
          on_track: response.data.on_track,
          total_budget: response.data.total_budget,
        }),
      );
      return response.data.campaigns;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaigns.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaigns");
    }
  },
);

export const fetchCampaignByIdData = createAsyncThunk(
  "campaigns/fetchCampaignByIdData",
  async (id: string, thunkAPI) => {
    try {
      const response = await campaignsApi.fetchCampaignByIdData(id);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaign.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaign");
    }
  },
);

export const editCampaignByIdData = createAsyncThunk(
  "campaigns/editCampaignByIdData",
  async (data: CampaignByIdEditDataType, thunkAPI) => {
    try {
      const response = await campaignsApi.editCampaignByIdData(data);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          heading: "Campaign saved",
          text: `${response.data.name} campaign is saved.`,
        }),
      );
      data.callback();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaign.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaign");
    }
  },
);

export const createCampaign = createAsyncThunk(
  "campaigns/createCampaign",
  async (data: CampaignCreateDataType, thunkAPI) => {
    try {
      const response = await campaignsApi.createCampaign(data.body);
      thunkAPI.dispatch(fetchCampaignsData(""));
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          heading: "Campaign saved",
          text: `${response.data.name} campaign is created.`,
        }),
      );
      data.callback();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaign.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaign");
    }
  },
);
export const deleteCampaignByIdData = createAsyncThunk(
  "campaigns/deleteCampaignByIdData",
  async (data: CampaignByIdDeleteType, thunkAPI) => {
    try {
      await campaignsApi.deleteCampaignByIdData(data.id);
      thunkAPI.dispatch(
        displayAlert({
          type: "success",
          heading: "Campaign deleted",
          text: `${data.name} campaign is deleted.`,
        }),
      );
      if (data.callback) {
        data.callback();
      }
      return data.id;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaign.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaign");
    }
  },
);

export const fetchInitialRecommendation = createAsyncThunk(
  "campaigns/fetchInitialRecommendation",
  async (_, thunkAPI) => {
    try {
      const response = await campaignsApi.fetchInitialRecommendation();
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaigns.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaigns");
    }
  },
);

export const fetchMediaPlanData = createAsyncThunk(
  "campaigns/fetchMediaPlanData",
  async (data: MediaPlanBodyDataType, thunkAPI) => {
    try {
      const response = await campaignsApi.fetchMediaPlanData(data);
      return response.data;
    } catch (e: unknown) {
      handleThunkError(
        e,
        thunkAPI.dispatch,
        "An unexpected error occurred while fetching campaigns.",
      );
      return thunkAPI.rejectWithValue("Failed to fetch campaigns");
    }
  },
);
